// Formatting helpers for the visitor-analytics surfaces (ORB-373). Shared by the
// Web / Campaign / Workspace tiles and the per-Contact timeline so counts, rates, and
// durations render identically everywhere.

/** How a metric's numeric value should be rendered. */
export type MetricFormat = 'count' | 'percent' | 'duration'

/** `1,234` (count), `12.5%` (percent), or `2m 30s` (duration). */
export function formatMetricValue(value: number, format: MetricFormat = 'count'): string {
  switch (format) {
    case 'percent':
      return formatPercent(value)
    case 'duration':
      return formatDuration(value)
    case 'count':
    default:
      return Math.round(value).toLocaleString()
  }
}

/** `12.5%` — one decimal, trimmed of a trailing `.0`. */
export function formatPercent(value: number): string {
  const rounded = Math.round(value * 10) / 10
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}%`
}

/** Humanise a duration in seconds: `0s`, `45s`, `2m 30s`, `1h 5m`. */
export function formatDuration(totalSeconds: number): string {
  const seconds = Math.max(0, Math.round(totalSeconds))
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) {
    const remSeconds = seconds % 60
    return remSeconds ? `${minutes}m ${remSeconds}s` : `${minutes}m`
  }
  const hours = Math.floor(minutes / 60)
  const remMinutes = minutes % 60
  return remMinutes ? `${hours}h ${remMinutes}m` : `${hours}h`
}

/**
 * Format a metric's window-over-window delta for the growth pill. Counts show the
 * absolute change (`1,234`); percent metrics show percentage points (`1.2 pts`);
 * durations are humanised. The sign/arrow is rendered by the tile, so this is unsigned.
 */
export function formatDelta(delta: number, format: MetricFormat = 'count'): string {
  const abs = Math.abs(delta)
  switch (format) {
    case 'percent': {
      const rounded = Math.round(abs * 10) / 10
      return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)} pts`
    }
    case 'duration':
      return formatDuration(abs)
    case 'count':
    default:
      return Math.round(abs).toLocaleString()
  }
}
